"use client";

import CircularOutlineIcon from "@/components/CircularButton/styled/CircularOutlineIcon";
import { FlexColumn, FlexRowAlignCenter } from "@/components/Flex";
import ChatLog from "./components/ChatLog";
import { iconSizes } from "@/utils/iconProps";
import { themeColors } from "@/utils/themeColors";
import { X } from "lucide-react";
import { ChatFlowState, ChatSuggestionOpt } from "./types";
import { KaiIcon } from "./components/KaiIcon";
import { useChatEditorCommunicator } from "./hooks/useChatEditorCommunicator";
import AIGenerationChatInput from "./components/AIGenerationChatInput";

const KaiChatHeader = ({ onClose }: { onClose?: () => void }) => {
    return (
        <FlexRowAlignCenter
            style={{
                background: themeColors.neutralWhite,
                justifyContent: "space-between",
                zIndex: 9,
                marginBottom: "1.5rem",
                padding: "0 2.5rem",
                flexShrink: 0,
            }}>
            <FlexRowAlignCenter style={{ gap: "12px" }}>
                <KaiIcon
                    imageDimension={{ width: 34.5, height: 25.3 }}
                    style={{ flexShrink: 0, borderRadius: "3.3rem", width: "4rem", height: "4rem" }}
                />
                <span
                    className="bodyBoldXl"
                    data-testid="auto-id-5a220b24-749b-4543-920c-0a820ea8ed73">Kai</span>
            </FlexRowAlignCenter>
            {onClose && <CircularOutlineIcon size={iconSizes.MD} Icon={X} radius="3.5rem" onClick={onClose} />}
        </FlexRowAlignCenter>
    );
};

const KaiChatInterface = ({
    placeholder = "Ask Kai",
    containerStyle,
    onClose,
    suggestionOptions,
    disabled,
    disabledReason,
}: {
    placeholder?: string;
    containerStyle?: React.CSSProperties;
    onClose?: () => void;
    suggestionOptions: {
        basic: ChatSuggestionOpt[];
        uponAnswerGenerated: ChatSuggestionOpt[];
        onSelected: ChatSuggestionOpt[];
    };
    disabled?: boolean;
    disabledReason?: string;
}) => {
    const chatFlowState = useChatEditorCommunicator(s => s.chatFlowState);

    return (
        <FlexColumn
            style={{
                backgroundColor: themeColors.neutralWhite,
                flex: 1,
                borderRadius: "2rem",
                height: "100%",
                padding: "2.5rem 0 1rem",
                minHeight: "calc(100vh - var(--navbar-height) - 13rem)",
                maxHeight: "calc(100vh - var(--navbar-height) - 13rem)",
                position: "relative",
                ...containerStyle,
            }}>
            <KaiChatHeader onClose={onClose} />
            <ChatLog
                chatSuggestions={{
                    basic: {
                        options: suggestionOptions.basic,
                        disabled: chatFlowState !== ChatFlowState.DEFAULT_ACTION,
                    },
                    uponAnswerGenerated: {
                        options: suggestionOptions.uponAnswerGenerated,
                        disabled: chatFlowState !== ChatFlowState.DECIDE_RESPONSE_ACTION,
                    },
                    onSelected: {
                        options: suggestionOptions.onSelected,
                        disabled: chatFlowState !== ChatFlowState.SELECTED_TEXT_ACTIONS,
                    },
                }}
                disabled={disabled}
            />
            <AIGenerationChatInput
                id="kai-chat-input"
                placeholder={placeholder}
                disabled={disabled}
                disabledReason={disabledReason}
            />
        </FlexColumn>
    );
};

export default KaiChatInterface;
